export const MARKETPLACE_TERMS_VERSION = '2026-08-artisan-first-v4';
export const REVIEW_POLICY_VERSION = '2026-07-25';

// Launch pricing: LPB earns only the service fee below. Stripe processing is
// billed separately by Stripe to the artisan's connected account.
export const PRICING_MODEL = 'artisan_first_direct_v3';
export const PLATFORM_SHARE_RATE = 0.10;
export const MIN_PLATFORM_SERVICE_EUR = 3.90;
export const MAX_PLATFORM_SERVICE_EUR = 500;
export const DEFAULT_DEPOSIT_RATE = 0.20;
export const DEFAULT_FINAL_RATE = 0.80;
export const DEFAULT_PAYMENT_COUNT = 2;
export const LARGE_PROJECT_THRESHOLD_EUR = 1000;

// Public Stripe France list prices used only for an indicative preview.
// The exact fee is always the amount returned by Stripe after payment.
export const STRIPE_EEA_CARD_RATE = 0.015;
export const STRIPE_EEA_CARD_FIXED_EUR = 0.25;
export const STRIPE_SEPA_DEBIT_FIXED_EUR = 0.35;
export const STRIPE_PRICE_REFERENCE_DATE = '2026-07-28';

export const percent = (value) => `${Math.round(Number(value || 0) * 100)} %`;
const money = (value) => Number((Number(value) || 0).toFixed(2));

export const platformFeeFor = (customerTotal = 0) => {
  const total = Math.max(0, money(customerTotal));
  if (!total) return 0;
  return money(Math.min(
    total,
    Math.max(
      MIN_PLATFORM_SERVICE_EUR,
      Math.min(MAX_PLATFORM_SERVICE_EUR, total * PLATFORM_SHARE_RATE),
    ),
  ));
};

// The artisan sets the final TTC quote paid by the client. LPB's service fee
// is deducted from that direct charge. Stripe processing is then charged by
// Stripe to the artisan connected account and is never LPB revenue.
export const protectedPrice = (customerTotal = 0) => {
  const total = Math.max(0, money(customerTotal));
  const platformServiceGross = platformFeeFor(total);
  const artisanBeforeStripe = money(Math.max(0, total - platformServiceGross));
  return {
    customerTotal: total,
    projectTotal: total,
    platformServiceGross,
    artisanBeforeStripe,
    artisanPayoutTarget: artisanBeforeStripe,
    effectiveShareRate: total ? money(platformServiceGross / total) : 0,
    stripeFeePayer: 'artisan',
  };
};

export const recommendedPaymentMethod = (customerTotal = 0) => (
  Number(customerTotal) >= LARGE_PROJECT_THRESHOLD_EUR ? 'sepa_debit' : 'card'
);

export const estimateStripeProcessing = (
  customerTotal = 0,
  method = 'card',
  paymentCount = DEFAULT_PAYMENT_COUNT,
) => {
  const total = Math.max(0, money(customerTotal));
  const count = Math.max(1, Math.round(Number(paymentCount) || 1));
  if (!total) return 0;

  if (method === 'sepa_debit') {
    return money(STRIPE_SEPA_DEBIT_FIXED_EUR * count);
  }

  // Standard EEA card estimate. Premium/international cards and optional
  // Stripe products can cost more, so the UI labels this as an estimate.
  return money((total * STRIPE_EEA_CARD_RATE) + (STRIPE_EEA_CARD_FIXED_EUR * count));
};

export const stripeFeeComparison = (
  customerTotal = 0,
  paymentCount = DEFAULT_PAYMENT_COUNT,
) => ({
  card: estimateStripeProcessing(customerTotal, 'card', paymentCount),
  sepaDebit: estimateStripeProcessing(customerTotal, 'sepa_debit', paymentCount),
  recommended: recommendedPaymentMethod(customerTotal),
});

export const feeBreakdown = (
  projectAmount = 0,
  method = recommendedPaymentMethod(projectAmount),
  paymentCount = DEFAULT_PAYMENT_COUNT,
) => {
  const pricing = protectedPrice(projectAmount);
  const stripeEstimate = estimateStripeProcessing(pricing.customerTotal, method, paymentCount);
  return {
    projectAmount: pricing.customerTotal,
    clientServiceFee: 0,
    clientTotal: pricing.customerTotal,
    artisanCommission: pricing.platformServiceGross,
    artisanNet: money(Math.max(0, pricing.artisanBeforeStripe - stripeEstimate)),
    artisanBeforeStripe: pricing.artisanBeforeStripe,
    stripeEstimate,
    platformGrossRevenue: pricing.platformServiceGross,
    stripeFeePayer: 'artisan',
  };
};

export const defaultPaymentPlan = (customerTotal = 0) => {
  const pricing = protectedPrice(customerTotal);
  const depositCustomer = money(pricing.customerTotal * DEFAULT_DEPOSIT_RATE);
  const depositPlatform = money(pricing.platformServiceGross * DEFAULT_DEPOSIT_RATE);
  return {
    pricing_model: PRICING_MODEL,
    pricing_version: MARKETPLACE_TERMS_VERSION,
    currency: 'eur',
    platform_service_rate: PLATFORM_SHARE_RATE,
    deposit_rate: DEFAULT_DEPOSIT_RATE,
    final_rate: DEFAULT_FINAL_RATE,
    customer_total: pricing.customerTotal,
    project_total: pricing.customerTotal,
    artisan_before_stripe_total: pricing.artisanBeforeStripe,
    platform_service_gross: pricing.platformServiceGross,
    deposit_customer_amount: depositCustomer,
    deposit_platform_service_amount: depositPlatform,
    deposit_artisan_before_stripe_amount: money(depositCustomer - depositPlatform),
    final_customer_amount: money(pricing.customerTotal - depositCustomer),
    final_platform_service_amount: money(pricing.platformServiceGross - depositPlatform),
    final_artisan_before_stripe_amount: money(
      pricing.artisanBeforeStripe - (depositCustomer - depositPlatform),
    ),
    stripe_fee_payer: 'artisan',
    charge_model: 'direct',
  };
};
